import { type RefObject, useEffect, useRef, useState } from 'react'

import {
  composeValue,
  padIfNonEmpty,
  parseSegmentValues,
} from '@ui-kit/components/formComponents/DateTimePicker/SegmentedDateTimePicker/helpers'
import type { SegmentedVariantProps } from '@ui-kit/components/formComponents/DateTimePicker/types'

type FocusableRef = { focus: () => void }

type UseSegmentedDateInputArgs = Pick<
  SegmentedVariantProps,
  'value' | 'onValueChange' | 'hideDay' | 'hideMonth' | 'hideYear'
>

type UseSegmentedDateInputRefs<TRef> = {
  monthRef: RefObject<TRef | null>
  yearRef: RefObject<TRef | null>
}

export const useSegmentedDateInput = <TRef extends FocusableRef>(
  args: UseSegmentedDateInputArgs,
  refs: UseSegmentedDateInputRefs<TRef>
) => {
  const hideDay = args.hideDay ?? false
  const hideMonth = args.hideMonth ?? false
  const hideYear = args.hideYear ?? false

  const initial = parseSegmentValues(args.value)
  const [day, setDay] = useState(hideDay ? '' : initial.day)
  const [month, setMonth] = useState(hideMonth ? '' : initial.month)
  const [year, setYear] = useState(hideYear ? '' : initial.year)
  const lastEmittedRef = useRef<string>(args.value)

  const [hasBlurred, setHasBlurred] = useState(false)
  const isFocusInsideRef = useRef(false)
  const blurTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => () => clearTimeout(blurTimerRef.current), [])

  useEffect(() => {
    if (args.value === lastEmittedRef.current) {
      return
    }
    const parsed = parseSegmentValues(args.value)
    setDay(hideDay ? '' : parsed.day)
    setMonth(hideMonth ? '' : parsed.month)
    setYear(hideYear ? '' : parsed.year)
    lastEmittedRef.current = args.value
  }, [args.value, hideDay, hideMonth, hideYear])

  const emitValue = (nextDay: string, nextMonth: string, nextYear: string) => {
    const value = composeValue(nextDay, nextMonth, nextYear)
    lastEmittedRef.current = value
    args.onValueChange(value)
  }

  const onSegmentFocus = () => {
    isFocusInsideRef.current = true
  }

  const handleSegmentBlur = () => {
    isFocusInsideRef.current = false

    clearTimeout(blurTimerRef.current)
    blurTimerRef.current = setTimeout(() => {
      if (!isFocusInsideRef.current) {
        setHasBlurred(true)
      }
    }, 0)
  }

  const onDayChange = (input: string) => {
    if (input.length > 2) {
      return
    }
    setDay(input)
    emitValue(padIfNonEmpty(input), month, year)
    if (refs.monthRef.current && input.length >= 2 && Number(input) > 0) {
      refs.monthRef.current.focus()
    }
  }

  const onMonthChange = (input: string) => {
    if (input.length > 2) {
      return
    }
    setMonth(input)
    emitValue(day, padIfNonEmpty(input), year)
    if (refs.yearRef.current && input.length >= 2 && Number(input) > 0) {
      refs.yearRef.current.focus()
    }
  }

  const onYearChange = (input: string) => {
    if (input.length > 4) {
      return
    }
    setYear(input)
    emitValue(day, month, input)
  }

  const onDayBlur = () => {
    setDay((current) => padIfNonEmpty(current))
    handleSegmentBlur()
  }

  const onMonthBlur = () => {
    setMonth((current) => padIfNonEmpty(current))
    handleSegmentBlur()
  }

  const onYearBlur = () => {
    handleSegmentBlur()
  }

  const isComplete =
    (hideDay || day !== '') && (hideMonth || month !== '') && (hideYear || year.length === 4)

  let fieldToAutoFocus: 'day' | 'month' | 'year' = 'day'
  if (hideDay) {
    fieldToAutoFocus = hideMonth ? 'year' : 'month'
  }

  return {
    day,
    month,
    year,
    fieldToAutoFocus,
    isComplete,
    hasBlurred,
    onDayChange,
    onMonthChange,
    onYearChange,
    onDayBlur,
    onMonthBlur,
    onYearBlur,
    onSegmentFocus,
  }
}
